import DeleteIcon from "@mui/icons-material/Delete";
import AlertIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { isEmpty } from "lodash";
import { Fragment, useCallback, useMemo, useState } from "react";
import { Controller, UseFormReturn } from "react-hook-form";

import { Box } from "@/Components/v2/atoms/Box";
import { IconButton } from "@/Components/v2/atoms/Button";
import { DollarTextField } from "@/Components/v2/atoms/TextField";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { HIDE_DECIMAL } from "@/hooks/useConfig";
import { FormatCurrencyFunction } from "@/hooks/useConfig/types";
import { ALL_CHANNELS_KEY } from "@/pages/scenario-builder/v3/api/constants";
import { isDefined } from "@/Utils/array-utils";
import { getMappedName } from "@/Utils/mapping-utils";

import { HistoricalSpendDTO } from "../../api/getHistoricalSpend";
import {
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledTableHeaderRow,
  StyledTableRow,
} from "../../components/StyledComponents";
import { bcpLayerKey, BCPNode } from "../../utils/bcp";
import { getBusinessUnitHierarchy } from "../../utils/formatBusinessUnitHeirachy";
import { SpendInputFormData, useScenarioForms } from "../scenario-form/ScenarioFormProvider";
import { SpendInput } from "../scenario-form/types";
import { getAllowedMaxSpendForConstraint } from "../scenario-form/utils/formValidationRules";
import { confirmDeleteBusinessUnit } from "./DeleteBusinessUnitConfirmationModal";

const ForecastGeographySpendsTable: React.FC<{
  form: UseFormReturn<SpendInputFormData>;
  disabled?: boolean;
  currencySymbol: string;
  formatCurrency: FormatCurrencyFunction;
  isConstraintsFromApiEnabled: boolean;
  historicalSpendsDTO: HistoricalSpendDTO[];
}> = ({
  form,
  disabled = false,
  currencySymbol,
  formatCurrency,
  isConstraintsFromApiEnabled,
  historicalSpendsDTO,
}) => {
  const theme = useTheme();
  const [collapsedPanels, setCollapsedPanels] = useState<string[]>([]);

  const values = form.watch();
  const {
    formState: { errors },
  } = form;

  const constraintsByBCPKey = useMemo(() => {
    const result = values.spendInputs.reduce(
      (acc, constraint, originalIndex) => {
        const bcpKey = bcpLayerKey(constraint);
        if (!acc[bcpKey]) {
          acc[bcpKey] = [];
        }
        acc[bcpKey].push({
          constraint,
          originalIndex,
          isError: isEmpty(errors.spendInputs?.[originalIndex]),
        });

        return acc;
      },
      {} as Record<
        string,
        {
          isError: boolean;
          constraint: SpendInput;
          originalIndex: number;
        }[]
      >
    );

    return result;
  }, [values.spendInputs]);

  const {
    handleSpendInputRowChange,
    handleRemoveSpendInputBusinessUnit,
    distinctBCP,
    spendConstraintsDTO,
  } = useScenarioForms();

  const { mapBcpName } = useDefaultNodeNameInfo();

  // Event Handlers
  const handleAccordionChange =
    (unitId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      if ((event.target as Element).closest("[data-delete-button]")) return;
      setCollapsedPanels((prev) =>
        isExpanded ? prev.filter((id) => id !== unitId) : [...prev, unitId]
      );
    };

  const handleDeleteConfirm = async (bcp: BCPNode) => {
    const confirmed = await confirmDeleteBusinessUnit({ theme });
    if (confirmed) {
      handleRemoveSpendInputBusinessUnit(bcp);
    }
  };

  const removeConstraint = (index: number, constraint: SpendInput) => {
    const bcpConstraints = values.spendInputs.filter(
      (input: SpendInput) =>
        input.brand === constraint.brand &&
        input.category === constraint.category &&
        input.product === constraint.product
    );

    // If this was the only constraint for the BCP, set the channel to empty instead so we keep the Business Unit table
    if (bcpConstraints.length === 1) {
      handleSpendInputRowChange({
        type: "update",
        index,
        value: { ...constraint, geography: "", channel: "", spend: 0 },
      });
      return;
    }

    handleSpendInputRowChange({
      type: "remove",
      index,
    });
  };

  const addConstraint = (constraint: SpendInput) => {
    handleSpendInputRowChange({
      type: "add",
      value: constraint,
    });
  };

  const handleGeographyChange = (constraint: SpendInput, value: string, index: number) => {
    handleSpendInputRowChange({
      index,
      type: "update",
      // Reset channel so user can re-select valid channels
      value: { ...constraint, geography: value, channel: "" },
    });
  };

  const handleChannelChange = (constraint: SpendInput, value: string, index: number) => {
    handleSpendInputRowChange({
      index,
      type: "update",
      value: { ...constraint, channel: value },
    });
  };

  const handleSpendChange = (constraint: SpendInput, value: number | undefined, index: number) => {
    handleSpendInputRowChange({
      index,
      type: "update",
      value: { ...constraint, spend: value ?? 0 },
    });
  };

  const getMaxSpendForInput = useCallback(
    (constraint: SpendInput) => {
      return getAllowedMaxSpendForConstraint(
        {
          brand: constraint.brand,
          category: constraint.category,
          product: constraint.product,
          channel: constraint.channel,
          geography: constraint.geography ?? null,
        },
        historicalSpendsDTO,
        spendConstraintsDTO,
        isConstraintsFromApiEnabled
      );
    },
    [historicalSpendsDTO]
  );

  return (
    <Box>
      <Fragment>
        {Object.entries(constraintsByBCPKey).map(([bcpKey, constraints]) => {
          const isExpanded = !collapsedPanels.includes(bcpKey);

          const bcp = distinctBCP?.get(bcpKey);
          if (!bcp) {
            console.error("BCP not found", bcpKey);
            return null;
          }
          const bcpConstraints = constraints.filter(
            ({ constraint }) =>
              constraint.brand === bcp.brand &&
              constraint.category === bcp.category &&
              constraint.product === bcp.product
          );

          const availableGeographies = new Set(
            historicalSpendsDTO
              .filter(
                (spend) =>
                  spend.marketing_spend.marketing_spend_node.brand === bcp.brand &&
                  spend.marketing_spend.marketing_spend_node.category === bcp.category &&
                  spend.marketing_spend.marketing_spend_node.product === bcp.product &&
                  isDefined(spend.marketing_spend.marketing_spend_node.geography)
              )
              .map((spend) => spend.marketing_spend.marketing_spend_node.geography!)
          );

          const hasError = constraints.some(({ isError: errors }) => errors);

          return (
            <StyledAccordion
              key={bcpKey}
              expanded={isExpanded}
              onChange={handleAccordionChange(bcpKey)}
            >
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  flexDirection: "row-reverse",
                  "& .MuiAccordionSummary-content": {
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    margin: 0,
                  },
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    marginRight: 1,
                  },
                  ...(hasError &&
                    !isExpanded && {
                      borderColor: "error.main",
                      borderWidth: 1,
                      borderStyle: "solid",
                      color: "black",
                      backgroundColor: "error.light",
                    }),
                }}
              >
                <Stack sx={{ flex: 1 }} direction="row">
                  <Typography variant="subtitle1" sx={{ fontWeight: 400, fontSize: "1rem" }}>
                    {getBusinessUnitHierarchy(bcp, mapBcpName)}
                  </Typography>
                  {hasError && !isExpanded && (
                    <Tooltip
                      title="One or more of these constraints are invalid"
                      placement="top"
                      arrow
                    >
                      <AlertIcon sx={{ color: "error.main", ml: 1, cursor: "pointer" }} />
                    </Tooltip>
                  )}
                </Stack>
                {!disabled && (
                  <IconButton
                    size="small"
                    data-delete-button
                    onClick={(_event) => handleDeleteConfirm(bcp)}
                    sx={{
                      color: "text.secondary",
                      "&:hover": {
                        color: "error.main",
                        backgroundColor: "error.light",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </StyledAccordionSummary>

              <StyledAccordionDetails>
                <StyledTableHeaderRow sx={{ py: 0.5 }}>
                  <Box flex="1">
                    <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                      Geography
                    </Typography>
                  </Box>
                  <Box flex="1">
                    <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                      Media Channel
                    </Typography>
                  </Box>
                  <Box flex="1">
                    <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                      Spend
                    </Typography>
                  </Box>
                  <Box width="48px">
                    <span />
                  </Box>
                </StyledTableHeaderRow>

                {/* Constraint Rows */}
                {constraints.map(({ constraint, originalIndex }) => {
                  const selectedChannels = bcpConstraints
                    .filter(
                      (bcpConstraint) =>
                        bcpConstraint.constraint.geography === constraint.geography &&
                        bcpConstraint.constraint.brand === bcp.brand &&
                        bcpConstraint.constraint.category === bcp.category &&
                        bcpConstraint.constraint.product === bcp.product
                    )
                    .map((bcpConstraint) => bcpConstraint.constraint.channel);

                  const isAllChannelsSelected = selectedChannels.some(
                    (channel) => channel === ALL_CHANNELS_KEY
                  );
                  const isNotAllChannelsSelected = selectedChannels.some(
                    (channel) => channel && channel !== ALL_CHANNELS_KEY
                  );

                  const channelsForGeography = historicalSpendsDTO
                    .filter(
                      (spend) =>
                        spend.is_node_channel_active &&
                        spend.marketing_spend.marketing_spend_node.brand === bcp.brand &&
                        spend.marketing_spend.marketing_spend_node.category === bcp.category &&
                        spend.marketing_spend.marketing_spend_node.product === bcp.product &&
                        spend.marketing_spend.marketing_spend_node.geography ===
                          constraint.geography
                    )
                    .map((spend) => spend.marketing_spend.channel);

                  const availableChannels = [
                    {
                      key: ALL_CHANNELS_KEY,
                      label: "All Channels",
                      disabled: isNotAllChannelsSelected || isAllChannelsSelected,
                    },
                    ...channelsForGeography.map((channel) => ({
                      key: channel,
                      label: getMappedName(channel) || channel,
                      disabled: isAllChannelsSelected || selectedChannels.includes(channel),
                    })),
                  ];

                  return (
                    <StyledTableRow key={`${bcpKey}-${constraint.geography}-${constraint.channel}`}>
                      <Box sx={{ display: "flex", flex: 1, pr: 1, pt: 1 }}>
                        <Box flex="1" pr={1}>
                          <Controller
                            name={`spendInputs.${originalIndex}.geography`}
                            control={form.control}
                            render={({ field, fieldState }) => (
                              <FormControl fullWidth size="small">
                                <Select
                                  {...field}
                                  onChange={(event) =>
                                    handleGeographyChange(
                                      constraint,
                                      event.target.value as string,
                                      originalIndex
                                    )
                                  }
                                  variant="standard"
                                  disabled={disabled}
                                  size="small"
                                  error={fieldState.error ? true : false}
                                >
                                  {Array.from(availableGeographies).map((geography) => (
                                    <MenuItem key={geography} value={geography}>
                                      {geography}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {fieldState.error && (
                                  <FormHelperText error>{fieldState.error.message}</FormHelperText>
                                )}
                              </FormControl>
                            )}
                          />
                        </Box>
                        <Box flex="1" pr={1}>
                          <Controller
                            name={`spendInputs.${originalIndex}.channel`}
                            control={form.control}
                            render={({ field, fieldState }) => (
                              <FormControl fullWidth size="small">
                                <Select
                                  {...field}
                                  onChange={(event) =>
                                    handleChannelChange(
                                      constraint,
                                      event.target.value,
                                      originalIndex
                                    )
                                  }
                                  variant="standard"
                                  disabled={disabled}
                                  size="small"
                                  error={fieldState.error ? true : false}
                                >
                                  {availableChannels.map((channel) => (
                                    <MenuItem
                                      key={channel.key}
                                      value={channel.key}
                                      disabled={channel.disabled}
                                    >
                                      {channel.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {fieldState.error && (
                                  <FormHelperText error>{fieldState.error.message}</FormHelperText>
                                )}
                              </FormControl>
                            )}
                          />
                        </Box>
                        <Box flex="1" px={0.5}>
                          <Controller
                            name={`spendInputs.${originalIndex}.spend`}
                            control={form.control}
                            render={({ field, fieldState }) => (
                              <DollarTextField
                                value={field.value}
                                onChange={(value) =>
                                  handleSpendChange(constraint, value, originalIndex)
                                }
                                onBlur={field.onBlur}
                                name={field.name}
                                fullWidth
                                size="small"
                                variant="standard"
                                allowNegative={false}
                                disabled={disabled || !constraint.channel}
                                placeholder={`Max: ${formatCurrency(getMaxSpendForInput(constraint) ?? 0, HIDE_DECIMAL)}`}
                                helperText={
                                  form.formState.errors.spendInputs?.[originalIndex]?.spend?.message
                                }
                                error={Boolean(fieldState.error)}
                                currencySymbol={currencySymbol}
                              />
                            )}
                          />
                        </Box>
                        <Box width="48px" display="center" justifyContent="center">
                          {!disabled && (
                            <IconButton
                              size="small"
                              onClick={() => removeConstraint(originalIndex, constraint)}
                              disabled={disabled}
                              sx={{
                                color: "text.secondary",
                                "&:hover": {
                                  color: "error.main",
                                  backgroundColor: "error.light",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </StyledTableRow>
                  );
                })}

                <StyledTableRow>
                  <Box flex="1" pr={1} pt={1}>
                    <FormControl fullWidth size="small">
                      <Select
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            addConstraint({
                              geography: e.target.value as string,
                              channel: "",
                              brand: bcp.brand,
                              category: bcp.category,
                              product: bcp.product,
                              spend: null!, // Set empty instead of zero so we can see placeholder value
                            });
                          }
                        }}
                        disabled={disabled}
                        displayEmpty
                        size="small"
                        variant="standard"
                        renderValue={(selected) => {
                          if (selected === "") {
                            return (
                              <span
                                style={{
                                  color: theme.palette.text.disabled,
                                  fontStyle: "italic",
                                }}
                              >
                                + Add Geography
                              </span>
                            );
                          }
                          return selected;
                        }}
                      >
                        {Array.from(availableGeographies).map((geography) => (
                          <MenuItem key={geography} value={geography}>
                            {geography}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box flex="1" px={0.5}>
                    <Box
                      sx={{
                        height: "36px",
                        display: "flex",
                        alignItems: "center",
                        color: "text.disabled",
                        fontSize: "1rem",
                        fontStyle: "italic",
                        paddingLeft: "12px",
                        border: "1px solid transparent",
                        borderRadius: "8px",
                      }}
                    >
                      Channel
                    </Box>
                  </Box>

                  <Box flex="1" px={0.5}>
                    <Box
                      sx={{
                        height: "36px",
                        display: "flex",
                        alignItems: "center",
                        color: "text.disabled",
                        fontSize: "1rem",
                        fontStyle: "italic",
                        paddingLeft: "12px",
                        border: "1px solid transparent",
                        borderRadius: "8px",
                      }}
                    >
                      Spend
                    </Box>
                  </Box>

                  <Box width="48px">
                    <span />
                  </Box>
                </StyledTableRow>
              </StyledAccordionDetails>
            </StyledAccordion>
          );
        })}
      </Fragment>
    </Box>
  );
};

export default ForecastGeographySpendsTable;
